/** Client-side attribute formatting for contract preview (InspectionV2 aligned). */

export function getAttributeNumericValue(attr: any): number | null {
    if (!attr) return null;
    for (const raw of [attr.Qty, attr.CalculatedValue, attr.AttributeValue]) {
        if (raw === undefined || raw === null || raw === "") continue;
        const num = Number(raw);
        if (!isNaN(num)) return num;
    }
    return null;
}

export function getAttrRecords(attributes: any[], name: string): any[] {
    return attributes.filter((a: any) => a.AttributeName === name);
}

/** Prefer the last record with usable data (handles duplicate attribute rows). */
export function getAttrObj(attributes: any[], name: string): any | undefined {
    const records = getAttrRecords(attributes, name);
    if (records.length === 0) return undefined;

    for (let i = records.length - 1; i >= 0; i--) {
        const attr = records[i];
        const qty = getAttributeNumericValue(attr);
        if (qty !== null && qty > 0) return attr;
        const val = attr.AttributeValue;
        if (val !== null && val !== undefined && String(val).trim() !== "") return attr;
    }

    return records[records.length - 1];
}

export function getAttrValue(attributes: any[], name: string): string {
    const records = getAttrRecords(attributes, name);
    if (records.length === 0) return "";

    for (let i = records.length - 1; i >= 0; i--) {
        const val = records[i].AttributeValue;
        if (val !== null && val !== undefined && String(val).trim() !== "") {
            return String(val).trim();
        }
    }

    const last = records[records.length - 1]?.AttributeValue;
    return last !== null && last !== undefined ? String(last) : "";
}

export function getAttrDisplay(attributes: any[], name: string): string {
    const val = getAttrValue(attributes, name);
    return val !== "" ? val : "N/A";
}

export function formatTearOffLayerDisplay(attributes: any[]): string {
    const raw = getAttrValue(attributes, "Tear Off Layer");
    // Match inspection-attributes create default ("1") and claims-estimate parseTearOffLayers fallback.
    if (!raw) return "1 layer";

    const lower = raw.toLowerCase();
    if (lower.includes("3rd") || lower.includes("3+") || lower.includes("3 layer")) return "3 layers";
    if (lower.includes("2nd") || lower.includes("2 layer")) return "2 layers";

    const n = parseInt(raw.replace(/\D/g, ""), 10);
    if (!isNaN(n)) {
        if (n === 0) return "0 layers";
        return n === 1 ? "1 layer" : `${n} layers`;
    }

    return raw;
}

export function formatPitchDisplay(attributes: any[]): string {
    const val = getAttrValue(attributes, "Roof Pitch");
    if (!val) return "N/A";

    try {
        const parsed = JSON.parse(val);
        if (Array.isArray(parsed)) {
            return parsed
                .map((p: any) => {
                    const pitch = p.pitch || "-";
                    const sq = p.squares || p.area;
                    return sq ? `${pitch} (${sq} SQ)` : pitch;
                })
                .join(", ");
        }
    } catch {
        /* plain string pitch */
    }

    return val;
}

function formatQtyDisplay(attributes: any[], name: string, unit = "EA"): string {
    const attr = getAttrObj(attributes, name);
    if (!attr) return "N/A";
    const qty = getAttributeNumericValue(attr);
    if (qty !== null && qty > 0) {
        const action = attr.Action ? ` (${attr.Action})` : "";
        return `${qty} ${unit}${action}`;
    }
    const val = attr.AttributeValue;
    return val !== undefined && val !== null && String(val).trim() !== "" ? String(val) : "N/A";
}

function isVentilationPainted(raw: string): boolean {
    if (!raw || raw === "N/A") return false;
    const lower = raw.toLowerCase();
    return lower !== "no" && lower !== "false" && lower !== "n/a";
}

function formatFurnaceVentSize(raw: string, legacyJackSize?: string): string {
    if (!raw) {
        if (legacyJackSize) return `Jack/Cap/Collar: ${legacyJackSize}"`;
        return "Jack/Cap/Collar: Standard";
    }

    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) {
            const lines = parsed
                .filter((entry: any) => Number(entry?.count) > 0)
                .map((entry: any) => `${entry.count || 1}x ${entry.size || "6 inch"}`);
            return lines.length > 0 ? `Jack/Cap/Collar: ${lines.join("; ")}` : "Jack/Cap/Collar: Standard";
        }
    } catch {
        /* plain string legacy value */
    }

    return `Jack/Cap/Collar: ${raw}`;
}

function isInactiveChimneyEntry(entry: any): boolean {
    const count = Number(entry?.count) || 0;
    const type = String(entry?.type || "").toLowerCase();
    const size = String(entry?.size || "").toLowerCase();
    return count <= 0 || type === "n/a" || size === "none";
}

function isInactiveSkylightEntry(entry: any): boolean {
    const count = Number(entry?.count) || 0;
    const type = String(entry?.type || "").toLowerCase();
    const size = String(entry?.size || "").toLowerCase();
    return count <= 0 || type === "none" || size === "none";
}

function formatChimneyDetails(raw: string, legacyRaw?: string): string {
    if (raw && raw.startsWith("[")) {
        try {
            const items = JSON.parse(raw);
            if (Array.isArray(items)) {
                const lines = items
                    .filter((entry) => !isInactiveChimneyEntry(entry))
                    .map((entry) => {
                        const count = Number(entry.count) || 1;
                        const color = entry.color ? `, ${entry.color}` : "";
                        return `${count}x ${entry.type} — ${entry.size}${color}`;
                    });
                if (lines.length > 0) return lines.join("; ");
            }
        } catch {
            /* ignore */
        }
    }

    if (legacyRaw && legacyRaw !== "N/A") return legacyRaw;
    return raw && raw !== "N/A" ? raw : "N/A";
}

function formatSkylightDetails(raw: string, legacyRaw?: string): string {
    if (raw && raw.startsWith("[")) {
        try {
            const items = JSON.parse(raw);
            if (Array.isArray(items)) {
                const lines = items
                    .filter((entry) => !isInactiveSkylightEntry(entry))
                    .map((entry) => {
                        const count = Number(entry.count) || 1;
                        const kit =
                            entry.flashingKit && entry.flashingKit !== "N/A" ? `, ${entry.flashingKit}` : "";
                        const notes = entry.notes ? ` — ${entry.notes}` : "";
                        return `${count}x ${entry.type} — ${entry.size}${kit}${notes}`;
                    });
                if (lines.length > 0) return lines.join("; ");
            }
        } catch {
            /* ignore */
        }
    }

    if (legacyRaw && legacyRaw !== "N/A") return legacyRaw;
    return raw && raw !== "N/A" ? raw : "N/A";
}

function formatSkylightReflash(skylightDetailsRaw: string, legacyReflash?: string): string {
    const summary = formatSkylightDetails(skylightDetailsRaw);
    if (summary !== "N/A") return summary;
    if (legacyReflash && legacyReflash !== "N/A") return legacyReflash;
    return "N/A";
}

/**
 * InspectionV2 shows R&R when gutter/downspout data exists but remove/install flags are false.
 * Map to Yes/No columns on the contract gutter grid.
 */
function formatGutterScopeFlag(
    removeFlag: string,
    installFlag: string,
    hasData: boolean,
    column: "remove" | "install",
): string {
    const remove = String(removeFlag).toLowerCase() === "true";
    const install = String(installFlag).toLowerCase() === "true";

    if (hasData && !remove && !install) {
        return "Yes";
    }

    if (column === "remove") return remove ? "Yes" : "No";
    return install ? "Yes" : "No";
}

function formatLengthDisplay(raw: string): string {
    const n = Number(raw);
    if (!raw || isNaN(n) || n <= 0) return "N/A";
    return `${n} LF`;
}

export function buildGutterScope(attributes: any[]) {
    const gutterLength = Number(getAttrValue(attributes, "Gutter Length")) || 0;
    const downspoutLength = Number(getAttrValue(attributes, "Downspout Length")) || 0;
    const gutterSize = getAttrValue(attributes, "Gutter Size");
    const downspoutSize = getAttrValue(attributes, "Downspout Size");

    const hasGutterData = gutterLength > 0 || gutterSize !== "";
    const hasDownspoutData = downspoutLength > 0 || downspoutSize !== "";

    const removeGutters = getAttrValue(attributes, "Remove Existing Gutters");
    const installGutters = getAttrValue(attributes, "Install New Gutters");
    const removeDownspouts = getAttrValue(attributes, "Remove Existing Downspouts");
    const installDownspouts = getAttrValue(attributes, "Install New Downspouts");

    return {
        RemoveExisting: formatGutterScopeFlag(removeGutters, installGutters, hasGutterData, "remove"),
        InstallNew: formatGutterScopeFlag(removeGutters, installGutters, hasGutterData, "install"),
        RemoveDownspouts: formatGutterScopeFlag(removeDownspouts, installDownspouts, hasDownspoutData, "remove"),
        InstallDownspouts: formatGutterScopeFlag(removeDownspouts, installDownspouts, hasDownspoutData, "install"),
        GutterLength: formatLengthDisplay(getAttrValue(attributes, "Gutter Length")),
        DownspoutLength: formatLengthDisplay(getAttrValue(attributes, "Downspout Length")),
        GutterSize: getAttrDisplay(attributes, "Gutter Size"),
        GutterColor: getAttrDisplay(attributes, "Gutter Color"),
        DownspoutSize: getAttrDisplay(attributes, "Downspout Size"),
        DownspoutColor: getAttrDisplay(attributes, "Downspout Color"),
    };
}

export function buildNailingVentilationScope(attributes: any[]) {
    const paintedSuffix = isVentilationPainted(getAttrValue(attributes, "Ventilation Painted"))
        ? " — Painted YES"
        : "";

    const stepFlashing = formatQtyDisplay(attributes, "Step Flashing", "LF");
    const plumbingJacks = formatQtyDisplay(attributes, "Plumbing Jacks");
    const plumbingVents =
        plumbingJacks !== "N/A" ? `${plumbingJacks}${paintedSuffix}` : `Yes${paintedSuffix}`;

    return {
        StepFlashing: stepFlashing !== "N/A" ? `${stepFlashing}${paintedSuffix}` : stepFlashing,
        RoofVentilation: `${getAttrValue(attributes, "Ventilation Type") || "N/A"}${paintedSuffix}`,
        PlumbingVents: plumbingVents,
        FurnaceVent: formatFurnaceVentSize(
            getAttrValue(attributes, "Furnace Vent Size"),
            getAttrValue(attributes, "Furnace Vent Jack Size") || undefined,
        ),
        ReflashChimney: formatChimneyDetails(
            getAttrValue(attributes, "Chimney Details"),
            getAttrValue(attributes, "Reflash Chimney") || undefined,
        ),
        ReflashSkylights: formatSkylightReflash(
            getAttrValue(attributes, "Skylight Details"),
            getAttrValue(attributes, "Reflash Skylights") || undefined,
        ),
        ReplaceSkylights: formatSkylightDetails(
            getAttrValue(attributes, "Skylight Details"),
            getAttrValue(attributes, "Skylights") || undefined,
        ),
    };
}
